import {IssueTypeToken} from '@github-ui/issue-type-token'
import {IssueOpenedIcon} from '@primer/octicons-react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueTypeAddedEvent$key} from './__generated__/IssueTypeAddedEvent.graphql'
import {IssueEventRationale} from './IssueEventRationale'
import styles from './IssueTypeEvent.module.css'
import {TimelineRow} from './row/TimelineRow'

type IssueTypeAddedEventProps = {
  queryRef: IssueTypeAddedEvent$key & {createdAt?: string}
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  repositoryNameWithOwner?: string
}

// Fragment moved and renamed to IssueEventRationale.tsx

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueTypeAddedEventFragment = graphql`
  fragment IssueTypeAddedEvent on IssueTypeAddedEvent {
    databaseId
    actor {
      avatarUrl(size: 64)
      login
      profileResourcePath
      __typename
      ... on Bot {
        isCopilot
        isAgent
        displayName
      }
      ... on User {
        name
      }
      ... on EnterpriseUserAccount {
        name
      }
      ... on Mannequin {
        name
      }
    }
    createdAt
    issueType {
      name
      color
    }
    intent {
      rationale
    }
  }
`

export function IssueTypeAddedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  repositoryNameWithOwner,
}: IssueTypeAddedEventProps) {
  const {actor, createdAt, issueType, databaseId, intent} = useFragment(IssueTypeAddedEventFragment, queryRef)

  if (!issueType) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId

  const getTooltipText = (isTextTruncated: boolean) => {
    return isTextTruncated ? issueType.name : undefined
  }

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={IssueOpenedIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.issueTypeAdded.leading}`}
        <div className={styles.issueTypeTokenWrapper}>
          <IssueTypeToken
            name={issueType.name}
            color={issueType.color}
            href={`/${repositoryNameWithOwner}/issues?q=type:"${issueType.name}"`}
            getTooltipText={getTooltipText}
            size="small"
          />
        </div>
        {intent?.rationale && (
          <IssueEventRationale
            rationale={intent.rationale}
            issueEventDatabaseId={String(databaseId)}
            target={issueType.name}
            analyticsAction="ISSUE_TYPE_ADDED"
          />
        )}
        {`${LABELS.timeline.issueTypeAdded.trailing} `}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
